import React, { Fragment } from "react"
import { NextSeo } from "next-seo"
import styled from "styled-components"
import Header from "~/components/dashboard/Layout/Header/OldAppHeader"
import DesktopContainer from "~/components/DesktopContainer"
import Spacer from "~/components/Spacer"
import Divider from "~/components/traffic-tickets/steps/Divider"
import Footer from "~/components/Footer"
import Via from "../public/static/images/png/logos/via.png"
import PostMates from "../public/static/images/png/logos/postmates.png"
import IconSaveMoney from "../public/static/images/svg/IconSaveMoney.svg"
import BallerPeaceOut from "../public/static/images/png/baller-piece-out.png"
import TicketGuardian from "../public/static/images/png/TicketGuardian.png"

const CardRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props) => (props.left ? "flex-start" : "flex-end")};
  width: 100%;
  padding-left: 1em;
  padding-right: 1em;
  ${(props) => props.theme.media.mobile`
    justify-content: center;
  `};
`

const Title = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  font-size: ${(props) => (props.small ? "2em" : "3.125em")};
  color: ${(props) => props.theme.colors.text.header};
  text-align: center;
`

const Subtitle = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  font-size: 1.125em;
  color: ${(props) => props.theme.colors.text.subContent};
  text-align: center;
`

const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-top: 3em;
  padding-bottom: 1em;
`

const CardImageContainer = styled.div`
  display: flex;
  width: 190px;
  height: 190px;
  ${(props) => props.theme.media.mobile`
    margin-bottom: 1.5em;
  `};
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    overflow: hidden;
  }
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  ${(props) => props.theme.media.mobile`
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `};
`

const Card = styled.div`
  display: flex;
  max-width: 360px;
  flex-direction: column;
  padding-left: 1em;
`

const CardTitle = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  color: ${(props) => props.theme.colors.primary};
  font-size: 1em;
  margin-bottom: 1em;
  ${(props) => props.theme.media.mobile`
    text-align: center;
  `};
`

const CardText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  color: ${(props) => props.theme.colors.text.header};
  font-size: 0.875em;
  max-width: 280px;
  ${(props) => props.theme.media.mobile`
    text-align: center;
  `};
`

const PartnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const PartnerLogoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const PartnerLogo = styled.img`
  max-width: 120px;
  max-height: 120px;
  width: auto;
  height: auto;
`

const VerticalDivider = styled.div`
  width: 1px;
  height: 100px;
  background-color: ${(props) => props.theme.colors.ui.inputBorder};
  margin-left: 3em;
  margin-right: 3em;
  ${(props) => props.theme.media.mobile`
    margin-left: 1.5em;
    margin-right: 1.5em;
  `};
`

const BUSINESS_POINTS = [
  {
    title: "THE RISK-FREE GUARANTEE",
    imageSrc: IconSaveMoney,
    text:
      "All our customers enjoy the WinIt risk-free guarantee: If we can’t get your ticket totally dismissed, we don’t charge you anything. It’s really that simple!",
  },
  {
    title: "PERSONAL ACCOUNT MANAGER",
    imageSrc: BallerPeaceOut,
    text:
      "Your business account is monitored and managed by a dedicated support agent. They’ll be your single point of contact for any inquiries or issues.",
  },
  {
    title: "AUTO-DISPUTE",
    imageSrc: TicketGuardian,
    text:
      "Once your account is set up, forget about managing parking tickets yourself. We’ll monitor every license plate in your fleet, and dispute every ticket as soon as it’s uploaded to the NYC servers. Never worry about late fees on your tickets again. If anything ever goes wrong and a penalty is incurred, we promise to cover it for you, as part of our risk-free guarantee.",
  },
]

const Business = () => {
  return (
    <>
      <NextSeo
        title="WinIt for Business"
        description="The simplest solution to managing your fleet's parking tickets."
        canonical="https://www.appwinit.com/business"
        additionalMetaTags={[
          {
            property: "description",
            content:
              "The simplest solution to managing your fleet's parking tickets.",
          },
        ]}
      />
      <Header />
      <DesktopContainer>
        <TitleContainer>
          <Title>For business</Title>
          <Subtitle>
            The simplest solution to managing your fleet&apos;s parking tickets.
          </Subtitle>
        </TitleContainer>
        <Spacer desktop={48} mobile={48} />
        {BUSINESS_POINTS.map((point, index) => (
          <Fragment key={point.title}>
            <CardRow left={index % 2}>
              <CardContainer>
                <CardImageContainer>
                  <img src={point.imageSrc} alt={point.title} />
                </CardImageContainer>
                <Card>
                  <CardTitle>{point.title}</CardTitle>
                  <CardText>{point.text}</CardText>
                </Card>
              </CardContainer>
            </CardRow>
            <Spacer desktop={48} mobile={48} />
          </Fragment>
        ))}
      </DesktopContainer>
      <Divider top={16} bottom={16} />
      <Spacer desktop={48} mobile={48} />
      <DesktopContainer>
        <PartnerContainer>
          <CardTitle>Let&apos;s work together</CardTitle>
          <Title small>Our Current Partners</Title>
          <Spacer desktop={96} mobile={96} />
          <PartnerLogoRow>
            <PartnerLogo src={Via} />
            <VerticalDivider />
            <PartnerLogo
              src={PostMates}
              alt="Postmates delivery partner using WinIt for Business"
            />
          </PartnerLogoRow>
          <Spacer desktop={72} mobile={72} />
        </PartnerContainer>
      </DesktopContainer>
      <Divider top={16} bottom={0} />
      <iframe
        title="Schedule WinIt Support"
        src="https://calendly.com/winit"
        scrolling="no"
        height="1300px"
        width="100%"
        frameBorder="0"
      />
      <Footer noMedia noDisclaimer />
    </>
  )
}

export default Business
